import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Shield, Info, List } from "lucide-react";
import "./Legal.css";

const CONTACT_EMAIL = "premiumprofitai@gmail.com";
const LAST_UPDATED = "April 2, 2026";

const SECTIONS = [
  {
    title: "What We Collect",
    content: (
      <>
        <p>
          We collect the information reasonably needed to operate Premium Profit, secure user
          accounts, and deliver the product features you choose to use.
        </p>
        <ul>
          <li>
            <strong>Account details:</strong> Your name, email address, approval status, and
            encrypted login credentials.
          </li>
          <li>
            <strong>Connected account data:</strong> Broker identifiers, trading records, balances,
            symbols, and position data made available through your authorized integrations.
          </li>
          <li>
            <strong>Usage and diagnostic data:</strong> Log entries, session events, feature usage,
            IP address, browser type, device metadata, and error reports.
          </li>
          <li>
            <strong>Support and communication history:</strong> Messages, issue reports, and
            account-related requests you send to us.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How We Use Information",
    content: (
      <>
        <p>
          We use personal and account-related data only for legitimate product, security, and
          support purposes connected to Premium Profit.
        </p>
        <ul>
          <li>
            <strong>Deliver the service:</strong> To show analytics, trading data, reports, and
            workflow features linked to your account.
          </li>
          <li>
            <strong>Manage access and security:</strong> To authenticate sessions, review approvals,
            detect abuse, and respond to incidents.
          </li>
          <li>
            <strong>Support and communicate:</strong> To answer requests, share service notices, and
            send critical account or policy updates.
          </li>
          <li>
            <strong>Improve the platform:</strong> To troubleshoot bugs, measure reliability, and
            refine the product experience.
          </li>
        </ul>
        <div className="legal-callout">
          <Info size={16} />
          <span>
            We do not sell your personal data. We use connected trading data to provide Premium
            Profit features, maintain security, and support the operation of the service.
          </span>
        </div>
      </>
    ),
  },
  {
    title: "Authentication and Referral Data",
    content: (
      <>
        <p>
          Some account flows in Premium Profit rely on third-party sign-in providers and referral
          attribution features.
        </p>
        <ul>
          <li>
            <strong>Social sign-in:</strong> If you sign in with providers such as Google or X, we
            may receive basic profile data such as your name, email address, avatar, and provider
            account identifier as permitted by that provider.
          </li>
          <li>
            <strong>Referral tracking:</strong> If you join with a referral code or share your
            referral link, we process referral relationships, commission records, and related
            account events to attribute rewards and prevent abuse.
          </li>
          <li>
            <strong>Provider terms:</strong> Your use of third-party authentication providers
            remains subject to the privacy policy and terms of the relevant provider.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "When We Share Data",
    content: (
      <>
        <p>
          We only share data when it is necessary to run Premium Profit, comply with legal
          obligations, or protect the service and its users.
        </p>
        <ul>
          <li>
            <strong>Infrastructure and vendors:</strong> Hosting, authentication, email, monitoring,
            and support providers may process data on our behalf under appropriate safeguards.
          </li>
          <li>
            <strong>Connected integrations:</strong> When you authorize a broker or third-party
            connection, relevant data may flow between Premium Profit and that provider.
          </li>
          <li>
            <strong>Legal and safety disclosures:</strong> We may disclose information where
            required by law, court order, regulatory request, or to investigate fraud, abuse, or
            security incidents.
          </li>
          <li>
            <strong>Business transfers:</strong> If Premium Profit is involved in a merger,
            acquisition, or asset sale, data may be transferred as part of that transaction subject
            to applicable law.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Storage and Security",
    content: (
      <>
        <p>
          We use administrative, technical, and organizational safeguards designed to protect the
          confidentiality and integrity of the data we store.
        </p>
        <ul>
          <li>
            Passwords are stored using strong one-way hashing, and sensitive credentials are
            protected with encryption or equivalent safeguards.
          </li>
          <li>
            Traffic between your browser and our systems is protected in transit using standard
            encryption protocols.
          </li>
          <li>
            Administrative access is limited to personnel who need it for operations, support, or
            security purposes.
          </li>
          <li>
            No system is perfectly secure, so you should also protect your own devices, passwords,
            and connected broker credentials.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Cookies and Local Storage",
    content: (
      <>
        <p>
          Premium Profit uses a limited set of cookies or browser storage mechanisms to keep the
          application functional and secure.
        </p>
        <ul>
          <li>
            <strong>Authentication and session state:</strong> To keep you signed in and protect
            access to your account.
          </li>
          <li>
            <strong>User preferences:</strong> To remember settings such as interface choices and
            basic workflow preferences.
          </li>
          <li>
            <strong>Operational storage:</strong> To support page behavior, temporary calculations,
            and secure product functionality.
          </li>
        </ul>
        <p>
          We do not use Premium Profit to run third-party advertising trackers that follow you
          across unrelated websites.
        </p>
      </>
    ),
  },
  {
    title: "Retention",
    content: (
      <>
        <p>
          We keep data only for as long as it is reasonably needed for service delivery, legal
          compliance, security, dispute handling, or internal record-keeping.
        </p>
        <ul>
          <li>
            <strong>Account records:</strong> Retained while your account remains active and for a
            reasonable period after closure where needed for security or compliance.
          </li>
          <li>
            <strong>Trading and activity records:</strong> Retained to provide historical views,
            investigations, and reporting functionality tied to your account.
          </li>
          <li>
            <strong>Logs and diagnostics:</strong> Retained for limited periods needed to monitor
            reliability, investigate incidents, and improve the service.
          </li>
        </ul>
        <p>
          When deletion is requested, we will remove or anonymize data from active systems within a
          reasonable period unless we are required to retain specific information by law, for fraud
          prevention, or for legitimate security purposes. Residual copies may remain temporarily in
          protected backups.
        </p>
      </>
    ),
  },
  {
    title: "Your Choices and Rights",
    content: (
      <>
        <p>
          Depending on your location and applicable law, you may have rights over the personal data
          we hold about you.
        </p>
        <ul>
          <li>
            <strong>Access:</strong> Ask what personal data we maintain about you.
          </li>
          <li>
            <strong>Correction:</strong> Request correction of inaccurate or incomplete account
            information.
          </li>
          <li>
            <strong>Deletion:</strong> Ask us to delete your account and associated personal data,
            subject to lawful retention needs.
          </li>
          <li>
            <strong>Objection or restriction:</strong> Ask us to limit certain processing where
            applicable.
          </li>
          <li>
            <strong>Portability:</strong> Request export of data that you have provided to us where
            technically feasible and legally required.
          </li>
        </ul>
        <p>
          To exercise a request, contact us using the details below. We may need to verify your
          identity before acting on the request.
        </p>
      </>
    ),
  },
  {
    title: "International Data Transfers",
    content: (
      <p>
        Your information may be processed in countries other than where you live. Where cross-border
        transfers occur, we use reasonable safeguards designed to protect personal data consistent
        with applicable law.
      </p>
    ),
  },
  {
    title: "Children's Privacy",
    content: (
      <p>
        Premium Profit is intended for adults. We do not knowingly collect personal data from anyone
        under 18. If we learn that a minor has created an account, we will take steps to disable the
        account and delete the associated personal data where appropriate.
      </p>
    ),
  },
  {
    title: "Policy Updates",
    content: (
      <>
        <p>
          We may revise this Privacy Policy to reflect product changes, legal developments, or
          updates to our data practices.
        </p>
        <p>
          When changes are material, we will provide notice through the platform, by email, or
          through another reasonable method before the updated policy takes effect where required.
        </p>
      </>
    ),
  },
  {
    title: "Contact Us",
    content: (
      <>
        <p>
          If you have any questions about this Privacy Policy or how we handle your data, please
          reach out:
        </p>
        <ul>
          <li>
            <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </li>
          <li>
            <strong>Platform:</strong> Use the in-app support channel accessible from your
            dashboard.
          </li>
        </ul>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <div className="legal-hero">
        <div className="legal-page-header">
          <div className="legal-page-title-row">
            <h1>
              <Shield />
              Privacy Policy
            </h1>
            <span className="legal-page-header-badge">{LAST_UPDATED}</span>
          </div>
          <p className="legal-page-header-copy">
            How Premium Profit collects, uses, stores, and shares account and trading-related data
            across the platform.
          </p>
        </div>

        <Link to="/login" className="legal-back-link">
          <ArrowLeft size={14} />
          Back
        </Link>
      </div>

      <div className="legal-card legal-toc">
        <div className="legal-toc-header">
          <div className="legal-toc-header-copy">
            <div className="legal-toc-label">
              <List />
              <span>Table of Contents</span>
            </div>
            <p>Use the quick links below to jump to the exact privacy topic you want to review.</p>
          </div>
          <span className="legal-toc-count">{SECTIONS.length} sections</span>
        </div>
        <div className="legal-toc-grid">
          {SECTIONS.map((s, i) => (
            <a key={i} href={`#privacy-${i + 1}`} className="legal-toc-item">
              <span className="legal-toc-num">{i + 1}</span>
              <span className="legal-toc-item-copy">
                <span className="legal-toc-item-title">{s.title}</span>
                <span className="legal-toc-item-meta">Open section</span>
              </span>
              <ArrowUpRight className="legal-toc-item-icon" size={15} />
            </a>
          ))}
        </div>
      </div>

      <div className="legal-card">
        <div className="legal-sections">
          {SECTIONS.map((s, i) => (
            <section key={i} className="legal-section" id={`privacy-${i + 1}`}>
              <div className="legal-section-heading">
                <span className="legal-section-num">{i + 1}</span>
                <h2>{s.title}</h2>
              </div>
              {s.content}
            </section>
          ))}
        </div>
      </div>

      <footer className="legal-footer">
        <p>Premium Profit</p>
        <span>Privacy Policy</span>
      </footer>
    </div>
  );
}
